/* eslint-disable no-shadow */
/**
 * Implements a mask to obscure the rest of the applications.
 * Pops a view into the center of the screen.
 * Layout allows for 3 places to inject content.
 * Uses a default template to conform to modal bootstrap classes
 * to leverage to bootstrap CSS for this component.
 */

import 'jquery-ui/ui/version';
import 'jquery-ui/ui/keycode';
import 'jquery-ui/ui/focusable';
import 'jquery-ui/ui/tabbable';
import { $, util, Layout, core } from '@glu/core';
import template from './modal.hbs';

const { LayerView } = core; // yes, this is really must be so.

let index = 1050;
const increment = 10;
const STACK = 'stack';

function incrementIndex() {
  index += increment;
  return index;
}

function decrementIndex() {
  index -= increment;
  return index;
}

export default LayerView.extend({
  template,

  className() {
    return `glu-modal ${this.options.classes ? this.options.classes.root : ''}`;
  },

  ui: {
    mask: '> .modal-backdrop',
    close: 'button.close',
    dialog: '> div > .modal-dialog',
    body: '> div > div > form > .modal-body',
    footer: '> div > div > form > .modal-footer',
    modal: '> .modal'
  },

  events: {
    'click .modal-footer button': 'handleButtonClick',
    keypress: 'callHandleKeyPress',
    keydown: 'callHandleKeyPress'
  },

  // explicit paths like this is to avoid the parent view/layout from pilfering
  // subviews elements.
  regions: {
    header: '> div > div > form > .modal-header',
    body: '> div > div > form > .modal-body',
    footer: '> div > div > form > .modal-footer'
  },

  attributes: {
    role: 'dialog',
    tabindex: '-1',
    'aria-labelledby': 'modal-title',
    'aria-describedby': 'modal-body'
  },

  initialize(options) {
    LayerView.prototype.initialize.call(this, options);
    this.maskFlag = options.mask !== false;
    this.type = options.type;
    this.message = options.message;
    this.title = options.title;
    this.icon = options.icon;
    this.modalClass = options.modalClass;
    this.buttons = options.buttons;
    this.stretch = options.stretch !== false;
  },

  hideMask() {
    this.ui.mask.hide();
    this.maskFlag = false;
  },

  showMask() {
    this.ui.mask.show();
    this.maskFlag = true;
  },

  toggleMask() {
    this.ui.mask.toggle();
    this.maskFlag = !this.maskFlag;
  },

  onRender() {
    if (this.maskFlag) {
      this.ui.mask.show();
    } else {
      this.ui.mask.hide();
    }

    this.$el.show(); // modal class has display:none this is necessary to override
    // however I think we should deprecate that rule in the CSS in favor of if the modal
    // exists it should be shown until it is removed from the DOM

    // This class is added to the body to disable scrolling behind the modal (taken from bootstrap)
    this.$el.parent().addClass('modal-open');

    if (this.bodyView) {
      this.body.show(this.bodyView); // use body view element over message object
    }

    if (this.stretch) {
      this.resizeHandle = util.bind(this.handleResize, this);
      window.addEventListener('resize', this.resizeHandle);
      this.ui.body.css('overflow', 'auto');
      this.ui.modal.css('overflow', 'hidden');
      this.handleResize();
    }

    this.determineFocus();

    if (this.collisionMode === STACK) {
      this.setIndex(incrementIndex());
    }

    this.trigger('show', this);
  },

  close() {
    if (this.isClosed) {
      return false;
    }

    this.$el.parent().removeClass('modal-open');

    if (this.stretch) {
      window.removeEventListener('resize', this.resizeHandle);
    }
    if (this.collisionMode === STACK) {
      decrementIndex();
    }

    LayerView.prototype.close.call(this);
    return undefined;
  },

  determineFocus() {
    const tabbables = this.getTabbables(this.$el);
    if (tabbables.length > 0) {
      tabbables.eq(0).focus();
    } else {
      this.ui.dialog.focus();
    }
  },

  templateHelpers() {
    const { theme: { legacyModal = {} } = {} } = this.options;

    return {
      type: this.type || 'standard',
      message: this.message,
      title: this.title,
      dialogIcon: this.icon,
      modalClass: this.modalClass,
      buttons: this.buttons,
      closeIcon: legacyModal.closeIcon || 'x'
    };
  },

  getTabbables($el) {
    // this is a really funky way to get all the elements in a single jquery object that has
    // the close button explicitly on the end of the array.  This is required for the proper
    // tabbing logic.
    const $tabbables = $el.find(':tabbable').not('.close');
    return $($.merge($tabbables, $el.find('.close')));
  },

  callHandleKeyPress(e) {
    // allow the handleKeyPress function to be used by dialog.js
    this.handleKeyPress(this.$el, e);
  },

  handleKeyPress($el, e) {
    if (e.keyCode !== $.ui.keyCode.TAB) {
      return undefined;
    }

    e.preventDefault();

    const arr = this.getTabbables($el);
    const targ = arr.filter(e.target);

    if (targ.length > 0) {
      const index = arr.index(targ);

      if (e.shiftKey) {
        if (index > 0) {
          return arr.eq(index - 1).focus();
        }
        return arr.eq(arr.length - 1).focus();
      }
      if (index < arr.length - 1) {
        return arr.eq(index + 1).focus();
      }
    }

    this.determineFocus($el); // pass-thru $el for dialog.js

    return undefined;
  },

  handleResize() {
    // in the case where our ui.body isn't set the offset is undefined. (ie. grid manage columns has this issue)
    if (this.ui.body.offset() !== undefined) {
      const offset = this.ui.body.offset().top - parseInt($(window).scrollTop(), 10);

      const footerHeight = this.ui.footer.outerHeight();

      const dialogBottomMargin = parseInt(this.ui.dialog.css('margin-bottom'), 10);

      const h = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

      const maxHeight = h - (offset + footerHeight + dialogBottomMargin);

      this.ui.body.css('max-height', `${maxHeight}px`);
    }
  },

  handleButtonClick(e) {
    const key = e.target.getAttribute('data-key');

    const button = this.buttons[key];

    if (button && button.callback && util.isFunction(button.callback)) {
      button.callback.call(this, e);
    } else if (button && button.callback && util.isString(button.callback) && util.isFunction(this[button.callback])) {
      this[button.callback].call(this, e);
    } else if (button && button.callback && util.isString(button.callback) && this.bodyView && util.isFunction(this.bodyView[button.callback])) {
      this.bodyView[button.callback].call(this.bodyView, e);
    }

    this.trigger('button:click', button, e);
  },

  setBody(view) {
    this.bodyView = view;
  },

  // overrides LayerView's set element and uses append vs prepend.
  setElement(element, delegate) {
    Layout.prototype.setElement.call(this, element, delegate);
    if (this.collisionMode === STACK) {
      $('body').append(this.$el);
    } else {
      $('body').prepend(this.$el);
    }
  },

  setIndex(index) {
    this.ui.mask.css('z-index', index);
    this.ui.modal.css('z-index', index + 1);
  }
});
